import classNames from "classnames";
import { FieldMetaProps } from "formik";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PatternFormat } from "react-number-format";
import { FieldError } from "./FieldError";
import InputLabel from "@mui/material/InputLabel";
import { Box } from "@mui/material";
export interface ITimeFieldProps {
  name: string;
  id?: string;
  className?: any;
  classes?: any;
  value?: string;
  disabled?: boolean;
  onChange?: (time: string, error?: string) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  placeholder?: string;
  placeholderChar?: string;
  keepCharPositions?: boolean;
  showMask?: boolean;
  meta?: Partial<FieldMetaProps<any>>;
  label?: string;
}

interface IFormaTimeObject {
  time: string;
  error: string;
}

// Pass in the user input string, validate and pass it back as a reformatted time string in HH:mm format.
export const formatTimeString = (event: any): IFormaTimeObject => {
  const eventPassed: boolean = event?.target?.value;

  const userInputStr = eventPassed
    ? event.target.value.replace(":", "").trim().padStart(4, "0")
    : event && typeof event === "string"
      ? event.replace(":", "").trim().padStart(4, "0")
      : undefined;
  if (!userInputStr) return event;

  // const userInputStr = event.target.value.replace(":", "").trim(); // Does not include ':'. Example: 1230
  let inputStr = ""; // This will be what will be displayed in the textfield
  const hours = parseInt(userInputStr.slice(0, -2));
  let str = userInputStr; // Used to manipulate final string of what the time should be

  const firstIndex = parseInt(userInputStr.slice(0, 1));
  const secondIndex = parseInt(userInputStr.slice(1, 2));
  const thirdIndex = parseInt(userInputStr.slice(2, 3));
  const fourthIndex = parseInt(userInputStr.slice(3, 4));

  const hours1 = parseInt(userInputStr.slice(0, 1));
  const hours2 = parseInt(userInputStr.slice(1, 2));
  const minutes = parseInt(userInputStr.slice(2, 5));
  let validationMessage = null;

  // Checks that the hours is not bigger than 24 or that the minutes is not bigger than 59
  if (userInputStr.length === 4 && ((hours2 >= 4 && hours1 >= 2 && !isNaN(fourthIndex)) || minutes > 59)) {
    validationMessage = "Hours exceeds 24 and/or minutes exceed 59";
    inputStr = userInputStr;
  } else if (userInputStr.length === 3 && isNaN(fourthIndex) && hours2 > 5) {
    // If the user inputs 3 characters, checks that the seecondIndex is less than 5
    // So, an example is if the user inputs 950 it becomes 09:50 but if the user inputs 960
    // an alert will come up. Note: the largest minutes value is 59
    validationMessage = "Minutes exceed 59";
    inputStr = userInputStr;
  } else {
    // Checks that the input is not of the following format: '1 :3 '      // This was used before when showMask was off but it isn't needed now since you put a space in the beginning of the file
    if (isNaN(fourthIndex) && str.length === 3 && !isNaN(secondIndex)) {
      //  || (isNaN(firstIndex) && str.length === 3)) {
      // If the user inputs 3 characters, prepend a 0 -> example: user inputs: 120 becomes 01:20
      if (hours < 10) {
        str = "0" + str;
        inputStr = str;
      }
    }
    // Checks if the length is 4 and that there are no spaces in the string
    else if (
      str.length === 4 &&
      !isNaN(firstIndex) &&
      !isNaN(secondIndex) &&
      !isNaN(thirdIndex) &&
      !isNaN(fourthIndex)
    ) {
      inputStr = str;
    } else {
      // Otherwise, TimeField will give an alert if there are white spaces in unusual places
      validationMessage = "Incorrect time format";
      inputStr = userInputStr;
    }
  }
  // Adds a colon between values. inputStr, for example, would be 1230. The formattedStr = 12:30
  const formattedStr = inputStr.slice(0, 2) + ":" + inputStr.slice(2);
  if (eventPassed) {
    event.target.value = formattedStr;
    event.target.setCustomValidity(validationMessage); // Set error state and message if triggered.
  }
  return { time: formattedStr, error: validationMessage };
};

const timeFieldStyles = (theme: any): any => ({
  root: {
    maxHeight: "24px",
    margin: 0,
    "& input": { width: "100%" },
    "&.time-input-container": {
      position: "relative",
      display: "inline-block",
      margin: "5px",
      "& label": {
        position: "relative",
        maxWidth: "calc(100% - 10px)",
        textAlign: "right",
        transform: "none",
        color: theme.custom.forms.pristineColor,
        transition: theme.transitions.create(["border-color", "color"]),
        fontSize: 12,
        lineHeight: "12px",
        margin: "5px 10px auto auto",
        width: "80px",
      },
      "& .input_control": {
        display: "flex",
      },
      "& .time-input": {
        boxSizing: "border-box",
        fontFamily: "Heebo, sans-serif",
        fontWeight: 300,
        width: "60px",
        height: "24px",
        padding: "2px",
        borderRadius: "2px",
        position: "relative",
        color: theme.custom.forms.inputText,
        backgroundColor: theme.custom.forms.inputBack,
        border: "1px solid " + theme.custom.forms.inputBorder,
        textAlign: "center",
        fontSize: 12,
        transition: theme.transitions.create(["border-color", "color"]),
        "&::placeholder, &::-webkit-input-placeholder": {
          color: theme.custom.forms.labels,
        },
        "&:hover": {
          borderColor: theme.custom.forms.pristineColor,
        },
        "&:focus": {
          outlineOffset: "none",
          outline: "none",
        },
      },
      "&.local .time-input": {
        color: theme.palette.secondary.light,
      },
      "&:after": {
        content: '""',
        transition: theme.transitions.create(["transform"]),
        left: 0,
        right: 0,
        bottom: 0,
        position: "absolute",
        transform: "scaleX(0)",
        borderBottom: "2px solid " + theme.custom.forms.focusedColor,
        pointerEvents: "none",
      },
    },

    "&.disabled": {
      opacity: 0.5,
      userSelect: "none",
      pointerEvents: "none",
    },
    "&.focused": {
      "& .label label": {
        color: theme.custom.forms.focusedColor,
      },
      "&.time-input-container .time-input": {
        border: "1px solid " + theme.custom.forms.pristineColor,
      },
      "&.time-input-container:after": {
        transform: "scaleX(1)",
      },
    },
    "&.dirty": {
      "& .label label": {
        color: theme.custom.forms.dirtyColor,
      },
      "&.time-input-container .time-input": {
        border: "1px solid " + theme.custom.forms.dirtyColor,
      },
    },
    "&.error": {
      "& .label label": {
        color: theme.custom.forms.errorColor,
      },
      "&.time-input-container": {
        "&:after": {
          transform: "scaleX(1)",
          borderBottom: "2px solid " + theme.custom.forms.errorColor,
        },
      },
    },
    "& .error-message": {
      maxWidth: "300px",
      padding: "0px 5px",
      fontWeight: 300,
      color: theme.palette.error.main,
    },
  },
});

export const TimeField = (props: ITimeFieldProps): JSX.Element => {
  const { name, id, className, label, placeholder, onChange, onBlur, onFocus, value, disabled, meta } = props;
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const disabledStyle: any = disabled ? { opacity: 0.5, userSelect: "none", pointerEvents: "none" } : null;

  // Track onChange event internally as user types (this value is unformatted)
  const [internalValue, setInternalValue] = useState<string>("00:00");
  useEffect(() => {
    setInternalValue(value ?? "00:00");
  }, [value]);

  const handleUpdateValue = (value: string, pad: boolean): { time: string; error: string | null } => {
    let unmaskedValue = pad ? value.replace(/[^0-9]/g, "") : value;
    const timeRegex = /^(0[0-9]|1[0-9]|2[0-3]):([0-5][0-9])$/;
    if (pad && unmaskedValue.length < 4) {
      if (unmaskedValue.length <= 2) {
        unmaskedValue = unmaskedValue.padEnd(4, "0");
      }
      unmaskedValue = unmaskedValue.padStart(4, "0");
    }
    const truncatedValue = unmaskedValue.slice(0, 4);
    const paddedValue = truncatedValue.replace(/^(\d{0,2})(\d{0,2})$/, (_, hh, mm) => {
      if (parseInt(hh) > 23) {
        hh = "23";
      }
      if (parseInt(mm) > 59) {
        mm = "59";
      }
      const paddedHours = hh ? (pad ? hh.padStart(2, "0") : hh) : "00";
      return `${paddedHours}:${mm.padStart(2, "0")}`;
    });
    setInternalValue(paddedValue);

    const error = timeRegex.test(paddedValue) ? null : "Invalid time/format";
    return { time: paddedValue, error: error };
  };

  // Track if formik value is dirty.
  const dirty: boolean = useMemo(() => {
    if (!meta) return false;
    const { initialValue, touched } = meta;
    return touched && value !== initialValue ? true : false;
  }, [meta, value]);

  const hasError = meta?.error ? true : false;
  return (
    <Box
      className={classNames(
        className,
        "time-input-container",
        disabled && "disabled",
        isFocused && "focused",
        dirty && "dirty",
        hasError && "error"
      )}
      sx={(theme) => timeFieldStyles(theme).root}
      style={{ ...disabledStyle }}
    >
      <div className="input_control">
        {label && <InputLabel htmlFor={"id-" + name + "-date"}>{label}</InputLabel>}
        <PatternFormat
          allowEmptyFormatting={true}
          format="##:##"
          mask="_"
          value={internalValue}
          onChange={(event) => {
            const { time, error } = handleUpdateValue(event.target.value, false);
            if (typeof onChange === "function") onChange(time, error);
          }}
          onBlur={(event) => {
            const { time, error } = handleUpdateValue(event.target.value, true);
            if (typeof onChange === "function") onChange(time, error);
            if (typeof onBlur === "function") onBlur(event);
          }}
          className={`${className} time-input`}
          disabled={disabled}
          name={name}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          pattern="[0-9:]+"
          placeholder={placeholder}
        />

        <FieldError meta={meta} />
      </div>
    </Box>
  );
};
